import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  EmailDocument,
  EmailDocumentDefault,
  EmailDocumentFieldOption,
  insightsHeader,
  Octopus,
} from '@maximizer/core/shared/domain';
import { map, Observable } from 'rxjs';
import { ContextService } from './context.service';

@Injectable()
export class EmailDocumentService {
  constructor(
    private readonly context: ContextService,
    private readonly http: HttpClient,
  ) {}

  delete(emailDocumentKeys: string[]): Observable<boolean> {
    const request: { [name: string]: unknown } = {};

    if (emailDocumentKeys) {
      emailDocumentKeys.forEach(
        (key, index) =>
          (request['EmailDocument.' + index] = { Data: { Key: key } }),
      );
    }

    return this.http
      .post<Octopus.Response>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
        {
          headers: this.getRequestHeaders(),
        },
      )
      .pipe(
        map((result) => {
          return result.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }

  getFieldOptions(): Observable<EmailDocumentFieldOption> {
    const request: Octopus.EmailDocumentFieldOptionRequest = {
      EmailDocument: {
        FieldOptions: {
          Category: [
            {
              Value: 1,
              DisplayValue: 1,
            },
          ],
          Result: [
            {
              Value: 1,
              DisplayValue: 1,
            },
          ],
          SecAccess: {
            Read: [
              {
                Key: {
                  Uid: 1,
                  Value: 1,
                },
                DisplayValue: 1,
              },
            ],
            Write: [
              {
                Key: {
                  Uid: 1,
                  Value: 1,
                },
                DisplayValue: 1,
              },
            ],
          },
        },
      },
    };

    return this.http
      .post<Octopus.EmailDocumentFieldOptionResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
        {
          headers: this.getRequestHeaders(),
        },
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.EmailDocumentFieldOptionMapper();
            return mapper.from(result);
          }
          return { category: [], result: [], readAccess: [], fullAccess: [] };
        }),
      );
  }

  getEmailDocumentById(id: string): Observable<EmailDocument | null> {
    const request: Octopus.EmailDocumentReadRequest = {
      EmailDocument: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: id,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            Name: 1,
            Description: 1,
            Category: 1,
            Result: 1,
            SecStatus: {
              CanRead: 1,
              CanUpdate: 1,
            },
            SecAccess: {
              Write: [
                {
                  Key: {
                    Uid: 1,
                    Value: 1,
                  },
                  DisplayValue: 1,
                },
              ],
              Read: [
                {
                  Key: {
                    Uid: 1,
                    Value: 1,
                  },
                  DisplayValue: 1,
                },
              ],
            },
          },
        },
      },
    };

    return this.http
      .post<Octopus.EmailDocumentResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
        {
          headers: this.getRequestHeaders(),
        },
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const document = result.EmailDocument.Data?.[0];
            if (!document) return null;

            const mapper = new Octopus.EmailDocumentMapper();
            return mapper.from(document);
          }
          return null;
        }),
      );
  }

  create(
    emailDocument: EmailDocument,
    binaryFileCleanup = true,
  ): Observable<string | null> {
    const request = {
      EmailDocument: {
        Data: {
          Key: emailDocument.id,
          Name: emailDocument.name,
          BinaryDataId: emailDocument.binaryDataId,
          DateTime: emailDocument.dateTime.toISOString(),
          Size: emailDocument.size,
          ParentKey: emailDocument.parentKey,
          Description: emailDocument.description ?? '',
          Category: emailDocument.category,
          Result: emailDocument.result,
          SecAccess: {
            Write: [
              {
                Key: {
                  UID: emailDocument.fullAccess,
                },
              },
            ],
            Read: [
              {
                Key: {
                  UID: emailDocument.readAccess,
                },
              },
            ],
          },
        },
        Options: {
          BinaryFileCleanup: binaryFileCleanup,
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.EmailDocumentWriteResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
        {
          headers: this.getRequestHeaders(),
        },
      )
      .pipe(
        map((response) => {
          if (
            response.Code === Octopus.ResponseStatusCode.Successful &&
            response.EmailDocument?.Data?.Key
          ) {
            return response.EmailDocument.Data.Key;
          }
          return null;
        }),
      );
  }

  update(doc: {
    key: string;
    name: string;
    description: string;
    // null clears the field — legacy EmailProperties sent null (not '') for a
    // cleared Category/Result dropdown.
    category: string | null;
    result: string | null;
    secAccessWrite: string;
    secAccessRead: string;
  }): Observable<boolean> {
    const request = {
      EmailDocument: {
        Data: {
          Key: doc.key,
          Name: doc.name,
          Description: doc.description,
          Category: doc.category,
          Result: doc.result,
          SecAccess: {
            Write: [
              {
                Key: {
                  UID: doc.secAccessWrite,
                },
              },
            ],
            Read: [
              {
                Key: {
                  UID: doc.secAccessRead,
                },
              },
            ],
          },
        },
      },
    };

    return this.http
      .post<Octopus.EmailDocumentWriteResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
        {
          headers: this.getRequestHeaders(),
        },
      )
      .pipe(
        map((response) => {
          return response.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }

  readEmailDefaultByUserAndType(
    type: string,
  ): Observable<EmailDocumentDefault | null> {
    const request = {
      EmailDocumentDefault: {
        Scope: {
          Fields: {
            Key: 1,
            Result: 1,
            Category: 1,
            SecAccess: {
              Write: [
                {
                  Key: {
                    Uid: 1,
                    Value: 1,
                  },
                  DisplayValue: 1,
                },
              ],
              Read: [
                {
                  Key: {
                    Uid: 1,
                    Value: 1,
                  },
                  DisplayValue: 1,
                },
              ],
            },
          },
        },
        Options: {
          Intent: type,
        },
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: '$CURRENTUSER()',
            },
          },
        },
      },
    };

    return this.http
      .post<Octopus.EmailDocumentDefaultResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
        {
          headers: this.getRequestHeaders(),
        },
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const document = result.EmailDocumentDefault.Data?.[0];
            if (!document) return null;

            const mapper = new Octopus.EmailDocumentDefaultMapper();
            return mapper.from(document);
          }
          return null;
        }),
      );
  }

  private getRequestHeaders(): { [header: string]: string } {
    return {
      [insightsHeader('entity')]: 'EmailDocument',
    };
  }
}
